// src/components/vendor/PickupTypeSelection.tsx

import React from 'react';
import { Zap, Package, ChevronLeft, AlertCircle } from 'lucide-react';

const MAX_BOXES_ALLOWED = 5;

interface PickupTypeSelectionProps {
    selectedType: 'quick' | 'detailed' | null;
    onSelectType: (type: 'quick' | 'detailed') => void;
    boxCount: number;
    onBoxCountChange: (count: number) => void;
}

const PickupTypeSelection: React.FC<PickupTypeSelectionProps> = ({
    selectedType,
    onSelectType,
    boxCount,
    onBoxCountChange,
}) => {
    return (
        <div className="space-y-6">
            <p className="text-sm text-gray-500 text-center">
                Choose how you'd like to create your pickup request
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Quick Pickup */}
                <button
                    type="button"
                    onClick={() => onSelectType('quick')}
                    className={`p-6 border-2 rounded-xl transition-all text-left group ${selectedType === 'quick'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                >
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Zap className="h-6 w-6 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900">Quick Pickup</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                        <li>✓ One-step process</li>
                        <li>✓ Fast and simple</li>
                        <li>✓ Best for small orders</li>
                    </ul>
                </button>

                {/* Detailed Pickup */}
                <button
                    type="button"
                    onClick={() => onSelectType('detailed')}
                    className={`p-6 border-2 rounded-xl transition-all text-left group ${selectedType === 'detailed'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                        }`}
                >
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <Package className="h-6 w-6 text-purple-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900">Detailed Pickup</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                        <li>✓ Pack items into boxes</li>
                        <li>✓ Track each package</li>
                        <li>✓ Best for large orders</li>
                    </ul>
                </button>
            </div>

            {/* Box count */}
            <div className="flex flex-col items-center">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Boxes <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => onBoxCountChange(Math.max(1, boxCount - 1))}
                        className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
                    >
                        −
                    </button>
                    <input
                        type="number"
                        min={1}
                        max={MAX_BOXES_ALLOWED}
                        value={boxCount}
                        onChange={(e) => onBoxCountChange(Math.max(1, Math.min(MAX_BOXES_ALLOWED, parseInt(e.target.value) || 1)))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => onBoxCountChange(Math.min(MAX_BOXES_ALLOWED, boxCount + 1))}
                        className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center"
                    >
                        +
                    </button>
                </div>
                {selectedType === 'detailed' && boxCount === 1 && (
                    <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Detailed pickup requires at least 2 boxes. Increase the box count or choose Quick pickup.
                    </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                    Each box gets a unique code. Max {MAX_BOXES_ALLOWED}.
                </p>
            </div>
        </div>
    );
};

export default PickupTypeSelection;